import { defineStore } from "pinia";
import { commClient } from "fw4-library";
import { IAppNavButton, IKeyValue, useAppStore } from "@/app/store/app/app-store";
import { i18n } from '@app/i18n';
import { IFileData } from "fw4-library";
import router from "@/app/router";
import { RouteLocationNormalized } from "vue-router";
import { usePublicStore } from "../../public/public-store";

export enum EmploymentType{
    zap = 'Z',
    upo = 'U',
    sp = 'SP'
    // 0 = zaposlen, 1 = upokojenec, 2 = S.P.
}

export enum PreapprovedStatus {
    approved = 'A', // Summit potrdil vlogo
    declined = 'D', // Summit zavrnil vlogo
    expired = 'E', // Vlogi pretekel datum veljavnosti
    signed = 'S', // Vloga oddana, čaka na potrditev/zavrnitev
    cancelled = 'C', // Uporabnik preklical vlogo
    inserted = 'I' // Vloga ni bila dokončno oddana (ni bila podpisana)
}

export enum IIdentificationType{
    posta = "PS",
    sls = "SLS",
    petrol = "PETROL",
}

export interface IVloga {
    VlogaId: number;
    StatusVloge: PreapprovedStatus | null;
    DatumOddaje: string;
    Vrednost: number;
    StatusOpis: string;
    DatumVeljavnosti: string;
    MestoIdentifikacije: IIdentificationType | null;
    PreapprovedNotificationSeen: boolean;
}

export interface IFinancialData {
    HadTRRExecution: undefined;
    HadBlockedTRR: undefined;
    HadLatePayment: undefined;
    HadBankruptcyProceedings: undefined;
    FinancialProductUsed: undefined;
    SumInstallments: null;
    ConfirmAccuracy: null;
    AverageSalary: number | null;
}

export interface IMarketingData {
    MainNotifications: boolean;
    MarketingSummit: boolean;
    // EmailChannel: boolean;
    // SMSChannel: boolean;
}

export interface IPersonalData {
    //Osnovni podatki
    Firstname: string;
    Surname: string;
    TaxNumber: number | null;
    Birthdate: Date | null;
    // Sex: string;
    Msisdn: string;
    Email: string;
    IsEmailVerified: boolean;
    IsIdentified: boolean;
    TermsAgreement: boolean;
    //osebni podatki
    PermanentResidence: boolean | undefined;
    IsSlovenianCitizen: boolean | undefined;
    Address: string;
    PostalCode: string | undefined;
    BirthCity: string;
    Citizen: number | undefined;
    HasTemporaryResidence: boolean | undefined;
    TemporaryAddress: string;
    TemporaryPostalCode: string;
    TemporaryForeignPostalCode: string | null;
    TemporaryCitizen: number | undefined;
    PoliticallyExposedPerson: boolean | undefined;
    PoliticalFunction: string | null;
    PIOSourceFunds: string | null;
    PIOFundsStatus: string | null;
    CountryRiskFundsStatus: string | null;
    CountryRiskSourceFunds: string | null;
    //podatki o zaposlitvi
    EmploymentType: EmploymentType | null;
    HasSlovenianIncome: boolean | undefined;
    EmployerName: string;
    EmployerAddress: string;
    EmployerPostalCode: string;
    HasSlovenianAccount: boolean | undefined;
    TRR: string;
    SlovenianTRR: boolean | undefined;
    //Osebna identifikacija
    PersonalDocumentType: string;
    IdNumber: string;
    DocumentIssuer: string;
    IssueDate: Date | null;
    ValidityDate: Date | null;
    // ValidityDate: any | null;
    PermanentValidity: boolean | undefined;
    HasContract: boolean | undefined;
    DataEditType: string;
    DataStatusOK: boolean;
    IsMarketingDataUpdated: boolean;
    // Finančni podatki
    Salary: number | null;
    Salary2: number | null;
    Salary3: number | null;
    //dodano 10/04
    IsCompanyTaxNumber: boolean | undefined;
    AfternoonSp: boolean | undefined;
    Files: IFileData[];
    CreditType: string | null;
    MinCredit: number | null;
    displayModal: boolean;
}

export interface IMaxCredit {
    MaxAvailable: number;
    MaxAvailableNoDocumentation: number;
    IsCalculated: boolean;
}

export interface IEmailChange{
    EmailChangeStep: EmailChangeStep;
    EmailNew: string;
}

export interface IDataHelpers {
    ShowChangePhone: boolean;
    ShowValidationModal: boolean;
    ShowChangeEmail: boolean;
    Code: string;
    ServerError: string;
    FinancialDataLoadedBase: boolean;
    SaveMessage: string;
    ShowConfirmData: boolean;
    Pay3: boolean;
    IsSuperFast: boolean;
    HideTermsAgreement: boolean;
    VlogaDownloadUrl: string;
    ShowPermanentValidity: boolean;
    AveragePay3: number;
}

export enum EmailChangeStep {
    enter_code = 1,
    enter_new_email = 2
}

export enum PhoneChangeStep{
    Code = 1,
    Phone = 2,
    NewPhone = 3
}

export interface IEmailChange{
    EmailChangeStep: EmailChangeStep;
    EmailNew: string;
}

export interface IPhoneNumberChange{
    PhoneChangeStep: PhoneChangeStep;
    Prefix: IKeyValue | undefined;
    Msisdn: number | null;
}

export interface IPersonalDataInitial{
    Firstname: string;
    Surname: string;
    Birthdate: Date | null;
    Address: string;
    PostalCode: string | undefined;
    BirthCity: string;
    Citizen: number | undefined;
    TemporaryAddress: string;
    TemporaryPostalCode: string;
    TemporaryCitizen: number | undefined;
    EmploymentType: EmploymentType | null;
    EmployerName: string;
    EmployerAddress: string;
    EmployerPostalCode: string;
    PoliticalFunction: string | null;
    PIOSourceFunds: string | null;
    PIOFundsStatus: string | null;
    CountryRiskFundsStatus: string | null;
    CountryRiskSourceFunds: string | null;
    PersonalDocumentType: string;
    IdNumber: string;
    DocumentIssuer: string;
    IssueDate: Date | null;
    ValidityDate: Date | null;
    Salary: number | null;
    Salary2: number | null;
    Salary3: number | null;
}

export interface IPreapprovedAppSign{
    showSign: boolean;
    signUrl: string;
}

function GetDefaultData(): IPersonalDataInitial {
    return {
        Firstname: "",
        Surname: "",
        Birthdate: null,
        Address: "",
        PostalCode: undefined,
        BirthCity: "",
        Citizen: undefined,
        TemporaryAddress: "",
        TemporaryPostalCode: "",
        TemporaryCitizen: undefined,
        EmploymentType: null,
        EmployerName: "",
        EmployerAddress: "",
        EmployerPostalCode: "",
        PoliticalFunction: null,
        PIOSourceFunds: null,
        PIOFundsStatus: null,
        CountryRiskFundsStatus: null,
        CountryRiskSourceFunds: null,
        PersonalDocumentType: "",
        IdNumber: "",
        DocumentIssuer: "",
        IssueDate: null,
        ValidityDate: null,
        Salary: null,
        Salary2: null,
        Salary3: null,
    }
}

function getDefaultFinancialData(): IFinancialData{
    return {
        HadTRRExecution: undefined,
        HadBlockedTRR: undefined,
        HadLatePayment: undefined,
        HadBankruptcyProceedings: undefined,
        FinancialProductUsed: undefined,
        ConfirmAccuracy: null,
        SumInstallments: null,
        AverageSalary: null
    }
}

function GetPersonalDataEditSteps(): IAppNavButton[]{
    return [
        {
            title: 'Osebni podatki',
            routerName: "osebni_podatki",
            completed: false
        },
        {
            title: 'Kontaktni podatki',
            routerName: "kontaktni_podatki",
            completed: false
        },
        {
            title: 'Podatki o zaposlitvi',
            routerName: "podatki_zaposlitev",
            completed: false
        },
        {
            title: 'Finančni podatki',
            routerName: "financni_podatki",
            completed: false
        },
        {
            title: 'Osebna identifikacija',
            routerName: "osebna_identifikacija",
            completed: false
        }
    ]
}

export interface IPreapprovedDocumentation{
    title: string;
    description: string;
}

export interface ICreditAmountAdditional{
    documentationNeeded: boolean;
    pay3Needed: boolean;
}

export interface ICreditAmountTypeInfo{
    S1: ICreditAmountAdditional;
    S2: ICreditAmountAdditional;
    S3: ICreditAmountAdditional;
    T1: ICreditAmountAdditional;
    T2: ICreditAmountAdditional;
    SP1: ICreditAmountAdditional;
}

export interface ICreditAmountAvailable{
    type: string;
    availableAmount: number;
}

interface IDataEditingState{
    PodatkiInitial: IPersonalDataInitial;
    Podatki: IPersonalData;
    FinancniPodatki: IFinancialData;
    FinancniPodatkiCopy: IFinancialData;
    MaxCreditData: IMaxCredit;
    DataHelpers: IDataHelpers;
    EmailChange: IEmailChange;
    PhoneChange: IPhoneNumberChange;
    MarketingData: IMarketingData;
    DocumentTypes: IKeyValue[];
    PreapprovedAppSign: IPreapprovedAppSign;
    // Vloga: IVloga;
    Vloga: IVloga;
    PersonalDataEditSteps: IAppNavButton[];
    CreditAmountTypeInfo: ICreditAmountTypeInfo;
    CreditAmountAvailable: ICreditAmountAvailable[]
}

export const useDataEditingStore = defineStore("dataEditing", {
    state: (): IDataEditingState => {
        return {
            PodatkiInitial: GetDefaultData(),
            Podatki: {
                //Osnovni podatki
                Firstname: "",
                Surname: "",
                TaxNumber: null,
                Birthdate: null,
                // Sex: "",
                Msisdn: "",
                Email: "",
                IsEmailVerified: false,
                IsIdentified: false,
                TermsAgreement: false,
                //osebni podatki
                PermanentResidence: undefined,
                IsSlovenianCitizen: undefined,
                Address: "",
                PostalCode: undefined,
                BirthCity: "",
                Citizen: undefined,
                HasTemporaryResidence: undefined,
                TemporaryAddress: "",
                TemporaryPostalCode: "",
                TemporaryForeignPostalCode: null,
                TemporaryCitizen: undefined,
                PoliticallyExposedPerson: undefined,
                PoliticalFunction: "",
                PIOSourceFunds: "",
                PIOFundsStatus: "",
                CountryRiskFundsStatus: "",
                CountryRiskSourceFunds: "",
                //podatki o zaposlitvi
                EmploymentType: null,
                HasSlovenianIncome: undefined,
                EmployerName: "",
                EmployerAddress: "",
                EmployerPostalCode: "",
                HasSlovenianAccount: true,
                TRR: "",
                SlovenianTRR: undefined,
                //Osebna identifikacija
                PersonalDocumentType: "",
                IdNumber: "",
                DocumentIssuer: "",
                IssueDate: null,
                ValidityDate: null,
                PermanentValidity: false,
                HasContract: false,
                DataEditType: "",
                DataStatusOK: false,
                IsMarketingDataUpdated: false,
                Salary: null,
                Salary2: null,
                Salary3: null,
                //dodano kasneje
                IsCompanyTaxNumber: undefined,
                AfternoonSp: undefined,
                Files: [],
                CreditType: null,
                MinCredit: null,
                displayModal: true
            },
            FinancniPodatki: getDefaultFinancialData(),
            FinancniPodatkiCopy: getDefaultFinancialData(),
            MaxCreditData: {
                MaxAvailable: 0,
                MaxAvailableNoDocumentation: 0,
                IsCalculated: false
            },
            DataHelpers: {
                ShowChangePhone: false,
                ShowValidationModal: false,
                ShowChangeEmail: false,
                Code: "",
                ServerError: "",
                FinancialDataLoadedBase: false,
                SaveMessage: "",
                ShowConfirmData: true,
                Pay3: false,
                IsSuperFast: false,
                HideTermsAgreement: false,
                VlogaDownloadUrl: "",
                ShowPermanentValidity: false,
                AveragePay3: 0
            },
            EmailChange: {
                EmailChangeStep: EmailChangeStep.enter_code,
                EmailNew: ""
            },
            PhoneChange:{
                PhoneChangeStep: PhoneChangeStep.Code,
                Prefix: { Key: "386", Value: "SI +386" },
                Msisdn: null
            },
            MarketingData: {
                MainNotifications: true,
                MarketingSummit: true,
                // EmailChannel: true,
                // SMSChannel: true,
            },
            DocumentTypes: [
                {
                    Key: "OS",
                    Value: "Osebna Izkaznica",
                },
                {
                    Key: "PL",
                    Value: 'Potni list'
                },
            ],
            PreapprovedAppSign: {
                showSign: false,
                signUrl: ""
            },
            // Vloga: []
            Vloga: {
                VlogaId: -1,
                StatusVloge: null,
                DatumOddaje: '',
                Vrednost: 0,
                StatusOpis: '',
                DatumVeljavnosti: "",
                MestoIdentifikacije: null,
                PreapprovedNotificationSeen: false
            },
            PersonalDataEditSteps: GetPersonalDataEditSteps(),
            CreditAmountTypeInfo: {
                S1: {
                    documentationNeeded: false,
                    pay3Needed: false,
                },
                S2: {
                    documentationNeeded: true,
                    pay3Needed: false,
                },
                S3: {
                    documentationNeeded: true,
                    pay3Needed: true,
                },
                T1: {
                    documentationNeeded: true,
                    pay3Needed: false,
                },
                T2: {
                    documentationNeeded: true,
                    pay3Needed: true,
                },
                SP1: {
                    documentationNeeded: true,
                    pay3Needed: true,
                },
            },
            CreditAmountAvailable: []
        }
    },

    actions: {
        ResetEmailChange(){
            this.EmailChange = {
                EmailChangeStep: EmailChangeStep.enter_code,
                EmailNew: ""
            }
        },

        //CHANGE EMAIL
        //Send Validation Code for Email change
        async SendPinEmailChange() {
            const serverComplete = (Message: any): void => {
                if (Message.Success) {
                    this.DataHelpers.ShowChangeEmail = true;
                }
                else{
                    this.DataHelpers.ServerError = i18n.global.t("GlobalErrors." + Message.Error);
                }
            };

            commClient.executeMethod("SendPinEmailChange", {}, serverComplete);
        },

        // ValidationCode check when changing email
        async CheckPinEmail(){
            const serverComplete = (Message: any): void => {
                if (Message) {
                    if (Message.Success) {
                        this.EmailChange.EmailChangeStep = EmailChangeStep.enter_new_email;
                    } else {
                        this.DataHelpers.ServerError = i18n.global.t("GlobalErrors." + Message.Error);
                    }
                }
            };

            commClient.executeMethod("sp.EmailChangeCheckCode", {
                "Code": this.DataHelpers.Code
            }, serverComplete);
        },

        //Last step of email change
        async EmailChangeFinal(){

            const serverComplete = (Message: any): void => {
                if (Message) {
                    if (Message.Success) {
                        this.DataHelpers.ShowChangeEmail = false;
                        this.Podatki.Email = this.EmailChange.EmailNew;
                        this.Podatki.IsEmailVerified = false;

                    } else {
                        this.DataHelpers.ServerError = i18n.global.t("GlobalErrors." + Message.Error);
                    }
                }
            };

            commClient.executeMethod("EmailChange", {
                "Firstname": this.Podatki.Firstname,
                "EmailNew": this.EmailChange.EmailNew,
                "Code": this.DataHelpers.Code
            }, serverComplete);
        },

        EmailVerifiedStreamReceived(Message: any){
            if (Message && Message[0] && Message[0].CallbackType == "EMAIL_VERIFY") {
                this.Podatki.IsEmailVerified = true;
                commClient.OnStreamDataReceived.RemoveCallback(this.EmailVerifiedStreamReceived);
            }
        },

        //To je za potrjevanje vseh prejetih Emailov  TODO: prestavi v appstore
        async EmailValidate(ValidationId: string){
            const serverComplete = (Message: any): void => {
                if (Message) {
                    if (Message.Success) {
                    } else {
                        this.DataHelpers.ServerError = i18n.global.t("GlobalErrors.url_expired");
                    }
                }
            };

            commClient.executeMethod("EmailValidate", {
                "EmailValidationId": ValidationId
            }, serverComplete);
        },

        //CHANGE PHONE NUMBER
        //Phone change send code
        async SendEmailPhoneChange(){
            const serverComplete = (Message: any): void => {
                if (Message) {
                    if (Message.Success) {
                        this.DataHelpers.ShowChangePhone = true;
                    } else {
                        this.DataHelpers.ServerError = i18n.global.t("GlobalErrors." + Message.Error);
                    }
                }
            };

            commClient.executeMethod("SendEmailPhoneChange", {
            }, serverComplete);
        },

        //Phone change check code
        async PhoneChangeCheckCode(){
            const serverComplete = (Message: any): void => {
                if (Message) {
                    if (Message.Success) {
                        this.PhoneChange.PhoneChangeStep = PhoneChangeStep.Phone;
                    } else {
                        this.DataHelpers.ServerError = i18n.global.t("GlobalErrors." + Message.Error);
                    }
                }
            };

            commClient.executeMethod("sp.PhoneChangeCheckCode", {
                "Code": this.DataHelpers.Code
            }, serverComplete);
        },

        //Potrdi spremembo
        async SendPinPhoneChange(){
            const serverComplete = (Message: any): void => {
                if (Message) {
                    if (Message.Success) {
                        this.PhoneChange.PhoneChangeStep = PhoneChangeStep.NewPhone;
                    } else {
                        this.DataHelpers.ServerError = i18n.global.t("GlobalErrors." + Message.Error);
                    }
                }
            };

            commClient.executeMethod("SendPinPhoneChange", {
                "PhoneNumberNew": this.PhoneChange.Prefix?.Key.toString().replace("+", "").trim() + this.PhoneChange.Msisdn!.toString(),
            }, serverComplete);
        },

        //Preveri kodo ki jo je dobil na prejšnjem koraku
        async PhoneNumberChange(){
            const serverComplete = (Message: any): void => {
                if (Message) {
                    if (Message.Success) {
                        this.DataHelpers.ShowChangePhone = false;

                    } else {
                        this.DataHelpers.ServerError = i18n.global.t("GlobalErrors." + Message.Error);
                    }
                }
            };

            commClient.executeMethod("PhoneNumberChange", {
                "MsisdnNew": this.PhoneChange.Prefix?.Key.toString().replace("+", "").trim() + this.PhoneChange.Msisdn!.toString(),
                "Code": this.DataHelpers.Code
            }, serverComplete);
        },
        //User Data load
        async UserDataLoad(){
            const appStore = useAppStore();

            const serverComplete = (Message: any): void => {
                if (Message) {
                    appStore.HideMainLoader();

                    if(Message.userData.PersonalData){
                        Object.assign(this.PodatkiInitial, Message.userData.PersonalData);
                        Object.assign(this.Podatki, Message.userData.PersonalData);

                        if(this.Podatki.TemporaryAddress != '' ){
                            this.Podatki.HasTemporaryResidence = true;
                        }

                        if(this.Podatki.IssueDate != null && this.Podatki.ValidityDate != null){
                            this.Podatki.PermanentValidity = false;	
                        } else if (this.Podatki.IssueDate != null && this.Podatki.ValidityDate == null){
                            this.Podatki.PermanentValidity = true;	
                        }else if(this.Podatki.IssueDate == null && this.Podatki.ValidityDate == null){

                            this.Podatki.PermanentValidity = false;
                        }
                        else {
                            this.Podatki.PermanentValidity = true;
                        }

                        if(this.Podatki.TRR != ""){
                            if(this.CheckTRR()){
                                this.Podatki.HasSlovenianAccount = true;
                            }
                            else{
                                this.Podatki.HasSlovenianAccount = false;
                            }
                            // if(this.Podatki.TRR!.startsWith('SI')){
                                
                            // }else {
                                
                            // }
                        }else{
                            // this.Podatki.HasSlovenianAccount = undefined;
                            //spremenjeno je v true zato ker hočejo po defaultu imeti true
                            this.Podatki.HasSlovenianAccount = true;
                        }

                        if(this.Podatki.EmployerPostalCode != ""){
                            if(this.Podatki.EmployerPostalCode != "9999"){
                                this.Podatki.HasSlovenianIncome = true;
                            }
                            else{
                                this.Podatki.HasSlovenianIncome = false;
                            }
                        }

                    }
                    if (Message.CreditData) {
                        Object.assign(this.MaxCreditData, Message.CreditData);
                    }
                    if (Message.userData.FinancialData){
                        this.DataHelpers.FinancialDataLoadedBase = true;

                        Object.assign(this.FinancniPodatki, Message.userData.FinancialData);
                        Object.assign(this.FinancniPodatkiCopy, Message.userData.FinancialData);
                    }
                    if (Message.userData.MarketingData){
                        Object.assign(this.MarketingData, Message.userData.MarketingData);

                        //preveri če je termsAgreement
                        if(this.MarketingData.MarketingSummit){
                            this.Podatki.TermsAgreement = true;
                            this.DataHelpers.HideTermsAgreement = true;
                        }
                    }

                    if(Message.TaxNumberData){
                        if (Message.TaxNumberData.ErrorText != "") {
                            this.DataHelpers.ServerError = Message.TaxNumberData.ErrorText;
                        } 
                        else {
                            this.Podatki.IsCompanyTaxNumber = Message.TaxNumberData.IsCompanyTax;
                            this.DataHelpers.ServerError = "";
                        }
                        //dodam če je sp??
                        if(this.Podatki.EmploymentType == EmploymentType.sp){
                            this.Podatki.AfternoonSp = false;
                        }else{
                            this.Podatki.AfternoonSp = true;
                        }
                    }
                    else{
                        this.DataHelpers.ServerError = "Prišlo je do napake pri preverjanju davčne številke.";
                    }

                    //preveri če je politilčno izpostavljena oseba
                    if (this.Podatki.PIOSourceFunds != '') {
                        this.Podatki.PoliticallyExposedPerson = true;
                    } else {
                        this.Podatki.PoliticallyExposedPerson = false;
                    };

                    //preveri starost + če je dopolnjeno 70let pokažemo trajno veljavnost na osebni identifikaciji
                    let today = new Date();
                    let birthDate = new Date(this.Podatki.Birthdate!);
                    let age = today.getFullYear() - birthDate!.getFullYear();
                    if(age >= 70){
                        this.DataHelpers.ShowPermanentValidity = true;
                    };
                }
            };

            appStore.ShowMainLoader();

            commClient.executeMethod("UserDataLoad", {

            }, serverComplete);
        },

        //ENEJ DODAJ V BE
        async FinancialDetailsGet() {

            const getComplete = (Message: any): void => {
                this.DataHelpers.Pay3 = Message.Salary3;
                this.DataHelpers.IsSuperFast = Message.IsSuperFast;
                router.push({name: 'dokumentacija', params: { flowType: router.currentRoute.value.params.flowType}});
            }

            commClient.executeMethod("FinancialDetailsGet",
            {
                OrderToken : router.currentRoute.value.params.id,
                // Installments: installmentStore.InstallmentNr, //nebo
                TaxNumber: this.Podatki.TaxNumber,
                Birthdate: this.Podatki.Birthdate,
                SlovenianCitizen: this.Podatki.IsSlovenianCitizen,
                HasSlovenianIncome: this.Podatki.HasSlovenianIncome,
                EmploymentType: this.Podatki.EmploymentType
            },
            getComplete);
        },

         //User Data SAVE
         async UserDataSave(){
                const appStore = useAppStore();
            this.DataHelpers.SaveMessage = '';
                appStore.ShowMainLoader();
            let resolveSave!: () => void;
            const savePromise = new Promise<void>((resolve) => {
                resolveSave = resolve;
            });
            
            const serverComplete = (Message: any): void => {
                if (Message) {
                    this.Podatki.DataStatusOK = true;

                    if(router.currentRoute.value.params.flowType == "vloga"){
                        router.push({name: 'dokumentacija', params: { flowType: router.currentRoute.value.params.flowType}});
                    }
                    else{
                        this.DataHelpers.SaveMessage = 'Spremembe so bile uspešno shranjene.';
                        router.push({name: "dashboard_dashboard" });
                    }
                }
                appStore.HideMainLoader();
                resolveSave();
            };

            // Reset PIO and risk country values

            if(!this.GetIsRiskCountry){
                this.Podatki.CountryRiskSourceFunds = null;
                this.Podatki.CountryRiskFundsStatus = null;
            }

            if(!this.Podatki.PoliticallyExposedPerson){
                this.Podatki.PoliticalFunction = null;
                this.Podatki.PIOSourceFunds = null;
                this.Podatki.PIOFundsStatus = null;
            }

            if(!this.Podatki.HasTemporaryResidence){
                this.Podatki.TemporaryAddress = "";
                this.Podatki.TemporaryPostalCode = "";
                this.Podatki.TemporaryForeignPostalCode = null;
                this.Podatki.TemporaryCitizen = undefined;
            }

            commClient.executeMethod("UserDataSave", {
                "Firstname": this.Podatki.Firstname,
                "Surname": this.Podatki.Surname,
                "Birthdate": this.Podatki.Birthdate,
                // "Sex": this.Podatki.Sex,
                "IsSlovenianCitizen": this.Podatki.IsSlovenianCitizen,
                "Address": this.Podatki.Address,
                "PostalCode": this.GetPostalCodeObject,
                "BirthCity": this.Podatki.BirthCity,
                "Citizen": this.GetCitizenObject,
                "TemporaryAddress": this.Podatki.TemporaryAddress,
                "TemporaryPostalCode": this.GetTemporaryPostalCodeObject,
                "TemporaryCitizen": this.GetTemporaryCitizenObject,
                "TemporaryForeignPostalCode": this.Podatki.TemporaryForeignPostalCode,
                "CountryRiskSourceFunds": this.Podatki.CountryRiskSourceFunds,
                "CountryRiskFundsStatus": this.Podatki.CountryRiskFundsStatus,
                "EmploymentType": this.Podatki.EmploymentType,
                "EmployerName": this.Podatki.EmployerName,
                "EmployerAddress": this.Podatki.EmployerAddress,
                "EmployerPostalCode": this.Podatki.EmployerPostalCode,
                "PIOFunction": this.Podatki.PoliticalFunction,
                "PIOSourceFunds": this.Podatki.PIOSourceFunds,
                "PIOFundsStatus": this.Podatki.PIOFundsStatus,
                // "HasSlovenianAccount": this.Podatki.HasSlovenianAccount,
                "TRR": this.Podatki.TRR,
                "PersonalDocumentType": this.GetDocumentType,
                "IdNumber": this.Podatki.IdNumber,
                "DocumentIssuer": this.Podatki.DocumentIssuer,
                "IssueDate": this.Podatki.IssueDate,
                "ValidityDate": this.Podatki.ValidityDate,
                // "PermanentValidity": this.Podatki.PermanentValidity,
                // "FinancialProductUsed": this.FinancniPodatki.FinancialProductUsed,
                "HadTRRExecution": this.FinancniPodatki.HadTRRExecution,
                "HadLatePayment": this.FinancniPodatki.HadLatePayment,
                "HadBlockedTRR": this.FinancniPodatki.HadBlockedTRR,
                "HadBankruptcyProceedings": this.FinancniPodatki.HadBankruptcyProceedings,
                "SumInstallments": this.FinancniPodatki.SumInstallments,
                "AverageSalary": this.FinancniPodatki.AverageSalary,
                "TermsAgreement": this.Podatki.TermsAgreement,
                "SendEmail": this.DidDataChange()
            }, serverComplete);

            return savePromise;
        },

        //financial data load
        async FinancialDataLoad() {
            const serverComplete = (Message: any): void => {
                if (Message) {
                    this.FinancniPodatki = Message;
                    this.DataHelpers.FinancialDataLoadedBase = true;
                }
            };

            commClient.executeMethod("sp.FinancialDataLoad", {
            }, serverComplete);
        },  

        //Marketing Save
        async MarketingSave() {
            this.DataHelpers.SaveMessage = '';

            const marketingSave = (): void => {
                this.Podatki.IsMarketingDataUpdated = true;
                this.DataHelpers.SaveMessage = 'Spremembe so bile uspešno shranjene.';
            };

            commClient.executeMethod("SettingsMarketingSave", {
                "MainNotifications": this.MarketingData.MainNotifications,
                "MarketingSummit": this.MarketingData.MarketingSummit,
                // "EmailChannel": this.MarketingData.EmailChannel,
                // "SMSChannel": this.MarketingData.SMSChannel,
            }, marketingSave);
        },

        async ResendValidationEmail(){
            const emailSent = (Message: any): void => {
                if (Message.Success) {
                    
                }
            };

            commClient.executeMethod("ResendEmailValidation", {
            }, emailSent);
        },

        signComplete(Message: any){
            if(Message && Message[0] && Message[0].Success){
                if (Message[0].Status == "complete") {
                    this.PreapprovedAppSign.showSign = false;
                    this.PreapprovedAppSign.signUrl = "";

                    router.push({name: 'zakljucekSuccess', params: { flowType: router.currentRoute.value.params.flowType}});
                }
            }
            else {
                //alert("Napaka pri podpisu.");
            }

            commClient.OnStreamDataReceived.RemoveCallback(this.signComplete);
        },

        signCompleteLater(Message: any){
            if(Message && Message[0] && Message[0].Success){
                if (Message[0].Status == "complete") {
                    this.Vloga.StatusVloge = PreapprovedStatus.signed;
                    this.Vloga.StatusOpis = "SIGNED";
                    this.PreapprovedAppSign.showSign = false;
                    this.PreapprovedAppSign.signUrl = "";
                }
            }

            commClient.OnStreamDataReceived.RemoveCallback(this.signCompleteLater);
        },

        async PreapprovedESign(){
            const uploadComplete = (Message: any): void => {
                if (Message) {  
                    if(Message.Success){
                        this.PreapprovedAppSign.signUrl = Message.Url;
                        this.DataHelpers.VlogaDownloadUrl = Message.VlogaDownloadUrl;
                    }
                    else{
                    }
                }
                else {
                }
            }

            let preapprovedSelected = this.CreditAmountAvailable.find((creditType: ICreditAmountAvailable) => creditType.type == this.Podatki.CreditType);

            this.PreapprovedAppSign.signUrl = "/StaticLoaders/loaderVloga.html";
            this.PreapprovedAppSign.showSign = true;

            commClient.OnStreamDataReceived.AddCallback(this.signComplete);

            commClient.executeMethod("CreateVlogaPredodobreno", {
                Ime: this.Podatki.Firstname,
                Priimek: this.Podatki.Surname,
                DavcnaSt: this.Podatki.TaxNumber,
                Telefon: this.Podatki.Msisdn,
                Email: this.Podatki.Email,
                CreditType: this.Podatki.CreditType,
                CreditAmount: preapprovedSelected!.availableAmount,
                Salary1: this.Podatki.Salary,
                Salary2: this.Podatki.Salary2,
                Salary3: this.Podatki.Salary3,
                SumInstallmentAmount: this.FinancniPodatki.SumInstallments == null ? 0 : this.FinancniPodatki.SumInstallments,
                Documentation: JSON.stringify(this.Podatki.Files.map((file: IFileData) => file.FileId))
            }, uploadComplete);
        },

        async PreapprovedEsignLater(){
            const uploadComplete = (Message: any): void => {
                if(Message.Success){
                    this.PreapprovedAppSign.signUrl = Message.EsignUrl;
                }
            }

            this.PreapprovedAppSign.signUrl = "/StaticLoaders/loaderVloga.html";
            this.PreapprovedAppSign.showSign = true;

            commClient.OnStreamDataReceived.AddCallback(this.signCompleteLater);

            commClient.executeMethod("PreapprovedESignLater", {
                VlogaId: this.Vloga.VlogaId
            }, uploadComplete);
        },

        CheckDavcna (numDavcna: number) {

            let davcna = numDavcna.toString();
            //preveri ali je številka
            if (!/^\d+$/.test(davcna))
                return false;    
            if (davcna[0] != "0") {
                if (davcna.length == 8) {
                    var contr = parseInt(davcna[0]) * 8 +
                        parseInt(davcna[1]) * 7 +
                        parseInt(davcna[2]) * 6 +
                        parseInt(davcna[3]) * 5 +
                        parseInt(davcna[4]) * 4 +
                        parseInt(davcna[5]) * 3 +
                        parseInt(davcna[6]) * 2;
                    contr = contr % 11;
                    contr = (11 - contr) % 10;
                    if (parseInt(davcna[7]) == contr) {
                        return true;
                    } else {
                        return false;
                    }
                } else {
                    return false;
                }
            } else {
                return false;
            }
        },

        CheckTRR() {
            var r = (98 - (parseInt(this.Podatki.TRR.substring(0, 13) + "00") % 97)).toString();
            if (r.length == 1)
                r = "0" + r;
            return this.Podatki.TRR == this.Podatki.TRR.substring(0, 13) + r;
        },

        CheckTRRForeign() {
            // najmanj 14 znakov, prvi dve črki, ostalo številke
            return (this.Podatki.TRR.replaceAll(" ", "").length > 14 && /^[A-Za-z]{2}[A-Za-z0-9]*$/.test(this.Podatki.TRR.replaceAll(" ", "")));
        },

        DidDataChange(){
            let didDataChange: boolean = false;

            for(let key of Object.keys(this.PodatkiInitial)){
                if((this.PodatkiInitial[key as keyof typeof this.PodatkiInitial] ? this.PodatkiInitial[key as keyof typeof this.PodatkiInitial]!.toString().toLowerCase() : this.PodatkiInitial[key as keyof typeof this.PodatkiInitial]!) != 
                   (this.Podatki[key as keyof typeof this.Podatki]! ? this.Podatki[key as keyof typeof this.Podatki]!.toString().toLowerCase() : this.Podatki[key as keyof typeof this.Podatki]!)){
                    Object.assign(this.PodatkiInitial, this.Podatki)
                    didDataChange = true;
                    break;
                }
            }
            return didDataChange;
        },

        DidFinancialDataChange(){
            let didDataChange: boolean = false;

            for(let key of Object.keys(this.FinancniPodatkiCopy)){
                if(this.FinancniPodatkiCopy[key as keyof typeof this.FinancniPodatkiCopy] != this.FinancniPodatki[key as keyof typeof this.FinancniPodatki]){
                    Object.assign(this.FinancniPodatkiCopy, this.FinancniPodatki)
                    didDataChange = true;
                    break;
                }
            }
            return didDataChange;
        },
        
        SetSlovenianCitizen(){
            this.Podatki.IsSlovenianCitizen = this.Podatki.Citizen == 204;
            this.Podatki.EmployerPostalCode = "";
        },

        SetEmployerPostalCode(value: boolean){
            if(this.Podatki.IsSlovenianCitizen && value != undefined && !value){
                this.Podatki.EmployerPostalCode = "9999";
            }
            else if(value != undefined){
                this.Podatki.EmployerPostalCode = "";
            }
        },

        async IBanChecker() {
            return new Promise((resolve, reject) => {
                const checkComplete = (Message: any): void => {
                    if (Message.Status == 'VALID') {         
                        resolve(true);
                    }else if(Message.Status == 'UNCHECKED') {
                        if ((this.Podatki.HasSlovenianAccount || this.Podatki.EmploymentType == EmploymentType.sp) && !this.CheckTRR()){
                            reject(Error(i18n.global.t("GlobalErrors.INVALID_IBAN")));
                        } else if(!this.CheckTRRForeign()){
                            reject(Error(i18n.global.t("GlobalErrors.INVALID_IBAN")));
                        }
                        else{
                            resolve(true);
                        }
                    } 
                    else {
                        reject(Error(i18n.global.t("GlobalErrors." + Message.ibanError)));
                    }
                };
    
                commClient.executeMethod("CheckIban",
                    {
                        IBAN: this.Podatki.HasSlovenianAccount ? 'SI56' + this.Podatki.TRR : this.Podatki.TRR 
                    },
                    checkComplete);
            });
        },

        SetTrr(){
            if(this.Podatki.SlovenianTRR != undefined || this.Podatki.EmploymentType == EmploymentType.sp) {
                this.Podatki.HasSlovenianAccount = true;         
            }else{
            }
        },

        //Get preapproved list
        async GetPreapprovedItem() {
            const getComplete = (Message: any): void => {
                if (Message) { 
                    this.Vloga = Message as IVloga;
                }
                else {
                }
            };

            commClient.executeMethod("sp.UserPreapprovedVlogaGet", {
            }, getComplete);            
        },

        async CancelPreapproved(vlogaId: number) {
            const getComplete = (Message: any): void => {
                //če je vloga preklicana skrijem
                if(Message.Success){
                    this.Vloga.StatusVloge = PreapprovedStatus.cancelled
                }

            };

            commClient.executeMethod("PreapprovedCancel", {
                VlogaId: vlogaId
            }, getComplete);            
        },
        setDataEditingStep(validationResult: boolean, to: RouteLocationNormalized, from: RouteLocationNormalized){
            const navItemIndexTo: number = this.PersonalDataEditSteps.findIndex((item: IAppNavButton) => item.routerName == to.name);
            const navItemIndexFrom: number = this.PersonalDataEditSteps.findIndex((item: IAppNavButton) => item.routerName == from.name);

            this.PersonalDataEditSteps[navItemIndexFrom].completed = validationResult;

            if(navItemIndexTo == -1 && navItemIndexFrom != this.PersonalDataEditSteps.length - 1 || navItemIndexTo != -1 && navItemIndexTo < navItemIndexFrom){
                return true;
            }
            else{
                return validationResult;
            }
        },
        ResetDataEditSteps(){
            this.PersonalDataEditSteps = GetPersonalDataEditSteps();
        },
        async PreapprovedCheck(){
            const appStore = useAppStore();

            const checkComplete = (Message: any): void => {
                appStore.HideMainLoader();
                
                if(Message.length > 0){
                    this.CreditAmountAvailable = Message as ICreditAmountAvailable[];
                    this.Podatki.CreditType = this.CreditAmountAvailable[0].type;
                }
                else{
                    router.push({ name: "zakljucekError", params: { flowType: router.currentRoute.value.params.flowType}});
                }
            };

            appStore.ShowMainLoader();

            commClient.executeMethod("PreapprovedCheck", {
                TaxNumber: this.Podatki.TaxNumber,
                Birthdate: this.Podatki.Birthdate,
                SlovenianCitizen: this.Podatki.IsSlovenianCitizen,
                HasSlovenianIncome: this.Podatki.HasSlovenianIncome,
                IsSP: this.Podatki.EmploymentType == EmploymentType.sp,
                AverageSalary: 0,
                SumInstallments: 0
            }, checkComplete);  
        },

        GetPreapprovedDocumentation(creditType: string): IPreapprovedDocumentation[]{
            if(this.Podatki.EmploymentType == EmploymentType.zap){
                if(creditType == "S2" && this.Podatki.HasSlovenianAccount){
                    return [
                        {
                            title: "Bančni izpisek",
                            description: "Iz dokumenta mora biti razviden podatek o višini priliva iz naslova plače."
                        }
                    ]
                }
                else if (creditType == "S2" && !this.Podatki.HasSlovenianAccount){
                    return [
                        {
                            title: "Plačilna lista",
                            description: "Priložite zadnjo plačilno listo, ki se ujema s spodaj vpisanim zneskom."
                        },
                    ]
                }
                else if (creditType == "S3"){
                    return [
                        {
                            title: "Bančni izpisek za zadnje 3 mesece",
                            description: "Iz dokumenta mora biti razviden podatek o višini priliva iz naslova plače za zadnje 3 mesece."
                        },
                        {
                            title: "Plačilna lista ali potrdilo CSD",
                            description: "Priložite zadnjo plačilno listo, ki se ujema s spodaj vpisanim zneskom ali potrdilo CSD o nakazilu starševskega nadomestila."
                        },                        
                    ];
                }
                else if(creditType == "T1"){
                    return [
                        {
                            title: "Bančni izpisek",
                            description: "Iz dokumenta mora biti razviden podatek o višini priliva iz naslova plače."
                        },
                        {
                            title: "Plačilna lista",
                            description: "Priložite zadnjo plačilno listo, ki se ujema s spodaj vpisanim zneskom."
                        },   
                        {
                            title: "Potrdilo o zaposlitvi",
                            description: "Potrdilo o zaposlitvi najmanj za čas obročnega plačila mora izdati delodajalec s sedežem v SLoveniji."
                        },
                        {
                            title: "Potrdilo o prijavi stalnega prebivališča v RS",
                            description: ""
                        }                                                              
                    ]
                }
                else if(creditType == "T2"){
                    return [
                        {
                            title: "Bančni izpisek za zadnje 3 mesece",
                            description: "Iz dokumenta mora biti razviden podatek o višini priliva iz naslova plače za zadnje 3 mesece."
                        },
                        {
                            title: "Plačilna lista",
                            description: "Priložite zadnjo plačilno listo, ki se ujema s spodaj vpisanim zneskom."
                        },
                        {
                            title: "Potrdilo o zaposlitvi",
                            description: "Potrdilo o zaposlitvi najmanj za čas obročnega plačila mora izdati delodajalec s sedežem v SLoveniji."
                        },
                        {
                            title: "Potrdilo o prijavi stalnega prebivališča v RS",
                            description: ""
                        }
                    ]

                }
            }
            else if(this.Podatki.EmploymentType == EmploymentType.upo){
                if(creditType == "S2"){
                    return [
                        {
                            title: "Bančni izpisek",
                            description: "Iz dokumenta mora biti razviden podatek o višini priliva iz naslova pokojnine nakazane s strani ZPIZ."
                        }
                    ];
                }
                else if(creditType == "S3"){
                    return [
                        {
                            title: "Bančni izpisek",
                            description: "Iz dokumenta mora biti razviden podatek o višini priliva iz naslova pokojnine nakazane s strani ZPIZ."
                        }
                    ];
                }
                else if(creditType == "T1"){
                    return [
                        {
                            title: "Bančni izpisek",
                            description: "Iz dokumenta mora biti razviden podatek o višini priliva iz naslova pokojnine nakazane s strani ZPIZ."
                        },
                        {
                            title: "Potrdilo o prijavi stalnega prebivališča v RS",
                            description: ""
                        }                        
                    ];
                }
                else if(creditType == "T2"){
                    return [
                        {
                            title: "Bančni izpisek",
                            description: "Iz dokumenta mora biti razviden podatek o višini priliva iz naslova pokojnine nakazane s strani ZPIZ."
                        },
                        {
                            title: "Potrdilo o prijavi stalnega prebivališča v RS",
                            description: ""
                        }
                    ];
                }
            }
            else if(creditType == "SP1"){
                return [
                    {
                        title: "Bančni izpisek za zadnje 3 mesece",
                        description: "Iz dokumenta morajo biti razvidni podatki o višini prejemkov iz opravljanja dejavnosti za zadnje 3 mesece."
                    }
                ];
            }

            return [];
        },
        DeleteUser(){
            const publicStore = usePublicStore();

            const deleteComplete = (Message: any): void => {
                if(Message.Success){
                    if(commClient.WebSocketConnected){
                        publicStore.userLogOut();
                        commClient.disconnectWebSockets();
                    }
            
                    this.$reset();
                    router.push({ name: "home"});  
                }
            };

            commClient.executeMethod("sp.UserDelete", {
                TaxNumber: this.Podatki.TaxNumber
            }, deleteComplete); 
        }
    },
    getters: {
        // GetProgressBarPercent(): number {
        //     let retVal: number = 0;
        //     if (this.Podatki.DataStatusOK) {
        //         retVal += 25;
        //     }
        //     if (this.Podatki.IsEmailVerified) {
        //         retVal += 25;
        //     }
        //     if (this.Podatki.IsMarketingDataUpdated) {
        //         retVal += 25;
        //     }
        //     if (this.MaxCreditData.IsCalculated) {
        //         retVal += 25;
        //     }
        //     return retVal;
        // },
        GetProgressBarPercent(): number {
            let retVal: number = 0;
            if (this.Podatki.DataStatusOK) {
                retVal += 33;
            }
            if (this.Podatki.IsEmailVerified) {
                retVal += 33;
            }
            if (this.Podatki.IsMarketingDataUpdated) {
                retVal += 33;
            }
            return retVal;
        },

        GetTemporaryCitizenObject() : any {
            const appStore = useAppStore();

            if(this.Podatki.TemporaryCitizen != undefined ){
                return JSON.stringify(appStore.CountryList.find((element: IKeyValue) => element.Key == this.Podatki.TemporaryCitizen));
            }

            return null
        },

        GetCitizenObject() : any {
            const appStore = useAppStore();

            if(this.Podatki.Citizen != undefined ){
                return JSON.stringify(appStore.CountryList.find((element: IKeyValue) => element.Key == this.Podatki.Citizen));
            }

            return null
        },

        GetPostalCodeObject() : any {
            const appStore = useAppStore();

            if(this.Podatki.PostalCode != undefined ){
                return JSON.stringify(appStore.PostalCodeList.find((element: IKeyValue) => element.Key == this.Podatki.PostalCode));
            }

            return null
        },

        GetTemporaryPostalCodeObject() : any {
            const appStore = useAppStore();

            if(this.Podatki.TemporaryPostalCode != "" ){
                return JSON.stringify(appStore.PostalCodeList.find((element: IKeyValue) => element.Key == this.Podatki.TemporaryPostalCode));
            }

            return null;
        },
    
        GetDocumentType() : any {
            if(this.Podatki.PersonalDocumentType != undefined ){
                return JSON.stringify(this.DocumentTypes.find((element: IKeyValue) => element.Key == this.Podatki.PersonalDocumentType));
            }
            return null
        },

        GetIsRiskCountry(): any {
            const appStore = useAppStore();

            const country: IKeyValue | undefined = appStore.CountryList.find((country: IKeyValue) => this.Podatki.Citizen == country.Key);
            const tempCountry: IKeyValue | undefined = appStore.CountryList.find((country: IKeyValue) => this.Podatki.TemporaryCitizen == country.Key);

            if ((this.Podatki.Citizen != undefined && country?.AdditionalData != null && country?.AdditionalData!["RiskDrzava"] == true) || (this.Podatki.TemporaryCitizen != undefined && tempCountry?.AdditionalData != null && tempCountry?.AdditionalData!["RiskDrzava"]) == true) {
                return true;
            }
            return false
        },

        // GetTveganaDrzava(): any {
        //     const appStore = useAppStore();

        //     const country: IKeyValue | undefined = appStore.CountryList.find((country: IKeyValue) => this.Podatki.Citizen == country.Key);
        //     const tempCountry: IKeyValue | undefined = appStore.CountryList.find((country: IKeyValue) => this.Podatki.TemporaryCitizen == country.Key);

        //     if ((this.Podatki.Citizen != undefined && country?.AdditionalData != null && country?.AdditionalData!["TveganaDrzava"] == true) || (this.Podatki.TemporaryCitizen != undefined && tempCountry?.AdditionalData != null && tempCountry?.AdditionalData!["TveganaDrzava"]) == true) {
        //         return true;
        //     }
        //     return false
        // },

        GetPostCodePodatkiZaposlitev() : IKeyValue[] {
            const appStore = useAppStore();
            
            if (this.Podatki.IsSlovenianCitizen == true) {
                if (this.Podatki.HasSlovenianIncome == true || this.Podatki.EmploymentType == EmploymentType.sp) {
                    return appStore.PostalCodeList.slice(0, appStore.PostalCodeList.length - 1);
                } else {
                    return appStore.PostalCodeList.slice(-1);
                }
            } else if(this.Podatki.IsSlovenianCitizen == false){
                return appStore.PostalCodeList.slice(0, appStore.PostalCodeList.length - 1);
            }
            else {
                return appStore.PostalCodeList;
            }
        },
    }
});
//TODO: za vse uspešne spremembe mail ali telefonske, se mora nekaj prikazati....KAJ?